import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { stackedBarWidths } from '@/lib/calculations/breakdownTotals'

/**
 * Alternate chart views for the analytics cards (White signal v2, Paper
 * "Humblytics · App v3 charts" → "App v3 · Chart alternate views · toggles").
 * The default view of each card is unchanged; these render only when the
 * card's view toggle is switched.
 *
 * Share math: callers pass each slice's `share` already taken against the
 * correct denominator (the period's deduped unique-visitor total for
 * breakdowns, the additive visit total for AI assistants). Shares can add up
 * to less than 100% (undetected dimension: the gap stays an empty track, never
 * a synthesized "Unknown" slice) or, on overlapping breakdowns, to more than
 * 100% (arc widths are scaled with `stackedBarWidths`, labels keep the true %).
 */

/** Top / focus slice orange, then the gray ramp, then the hairline. One orange per chart. */
export const SHARE_SLICE_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--border)',
]

// ─── View toggle ────────────────────────────────────────────────────────────

export type ChartViewIcon = 'bars' | 'donut' | 'mix' | 'gauge' | 'table' | 'area' | 'bar'

function ViewIcon({ icon }: { icon: ChartViewIcon }) {
  const fill = 'currentColor'
  switch (icon) {
    case 'bars':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <rect x="1.5" y="2" width="10" height="2" rx="1" fill={fill} />
          <rect x="1.5" y="5.5" width="7" height="2" rx="1" fill={fill} />
          <rect x="1.5" y="9" width="4" height="2" rx="1" fill={fill} />
        </svg>
      )
    case 'table':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <rect x="1.5" y="2" width="10" height="2" rx="1" fill={fill} />
          <rect x="1.5" y="5.5" width="10" height="2" rx="1" fill={fill} />
          <rect x="1.5" y="9" width="10" height="2" rx="1" fill={fill} />
        </svg>
      )
    case 'donut':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <circle cx="6.5" cy="6.5" r="4.5" stroke={fill} strokeWidth="2" />
        </svg>
      )
    case 'mix':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <rect x="1" y="5" width="5" height="3" rx="1" fill={fill} />
          <rect x="7" y="5" width="3" height="3" rx="1" fill={fill} />
          <rect x="11" y="5" width="1.5" height="3" rx="0.75" fill={fill} />
        </svg>
      )
    case 'gauge':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <path d="M2 10 A4.5 4.5 0 0 1 11 10" stroke={fill} strokeWidth="2" />
        </svg>
      )
    case 'area':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <path d="M1 10 L4 6 L7 8 L12 3" stroke={fill} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      )
    case 'bar':
      return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
          <rect x="1.5" y="6" width="2.5" height="5.5" rx="0.8" fill={fill} />
          <rect x="5.25" y="2" width="2.5" height="9.5" rx="0.8" fill={fill} />
          <rect x="9" y="4.5" width="2.5" height="7" rx="0.8" fill={fill} />
        </svg>
      )
  }
}

export interface ChartViewOption<T extends string> {
  value: T
  label: string
  icon: ChartViewIcon
}

/**
 * Icon-only view toggle, same anatomy as `OverviewTabs`: gray track with a
 * 2px pad, white active chip with a 1px drop, inactive icons in muted gray.
 * Never orange. Sits top-right of the card header.
 */
export function ChartViewToggle<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: ChartViewOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Chart view"
      className={cn('flex shrink-0 rounded-md bg-foreground/[0.05] p-0.5', className)}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            title={opt.label}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-[22px] w-[26px] items-center justify-center rounded-[5px] transition-colors',
              active
                ? 'bg-card text-foreground shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <ViewIcon icon={opt.icon} />
          </button>
        )
      })}
    </div>
  )
}

// ─── Slices ─────────────────────────────────────────────────────────────────

export interface ShareSlice {
  key: string
  label: ReactNode
  /** Count shown in the legend */
  value: number
  /** Percent of the caller's denominator; null hides the % (e.g. a folded "Other") */
  share: number | null
  /** Arc weight when `share` is null (a folded remainder still takes up space) */
  weight?: number
  /** Optional leading mark in the legend (e.g. an assistant logo) */
  leading?: ReactNode
}

/**
 * Keep the first `max - 1` slices and fold the rest into "Other": summed
 * count, arc weight from the summed shares, and no % label (the folded rows can
 * overlap, so their sum is not a share of anything).
 */
export function foldSlices(slices: ShareSlice[], max = 5): ShareSlice[] {
  if (slices.length <= max) return slices
  const head = slices.slice(0, max - 1)
  const rest = slices.slice(max - 1)
  return [
    ...head,
    {
      key: '__other',
      label: 'Other',
      value: rest.reduce((sum, s) => sum + s.value, 0),
      share: null,
      weight: rest.reduce((sum, s) => sum + (s.share ?? s.weight ?? 0), 0),
    },
  ]
}

function sliceWeights(slices: ShareSlice[]): number[] {
  return stackedBarWidths(slices.map((s) => Math.max(0, s.share ?? s.weight ?? 0)))
}

function formatSliceShare(share: number): string {
  if (share > 0 && share < 1) return '<1%'
  return `${Math.round(share)}%`
}

interface ArcSegment {
  key: string
  d: string
  color: string
}

/** Arcs between `start` and `start + sweep·weight`, 2 × `gap` rad between neighbours. */
function buildArcs(
  weights: number[],
  opts: { cx: number; cy: number; r: number; start: number; sweep: number; gap: number; flipY: boolean },
  keys: string[],
): ArcSegment[] {
  const { cx, cy, r, start, sweep, gap, flipY } = opts
  const point = (a: number) => {
    const x = cx + r * Math.cos(a)
    const y = flipY ? cy - r * Math.sin(a) : cy + r * Math.sin(a)
    return `${x.toFixed(2)} ${y.toFixed(2)}`
  }
  const visible = weights.map((w, i) => ({ w, i })).filter((s) => s.w > 0)
  const segs: ArcSegment[] = []
  let cursor = start
  const dir = flipY ? -1 : 1
  visible.forEach(({ w, i }, n) => {
    const span = (w / 100) * sweep
    const lead = n === 0 ? 0 : gap
    const tail = n === visible.length - 1 && w >= 99.9 ? 0 : gap
    const a0 = cursor + dir * lead
    const a1 = cursor + dir * Math.max(span - tail, lead + 0.001)
    cursor += dir * span
    if (Math.abs(a1 - a0) <= 0.002) return
    // A full 100% ring can't be drawn as one arc; split it in two
    if (span >= 2 * Math.PI - 0.001) {
      const mid = a0 + dir * Math.PI
      segs.push({
        key: keys[i],
        d: `M ${point(a0)} A ${r} ${r} 0 0 1 ${point(mid)} A ${r} ${r} 0 0 1 ${point(a1)}`,
        color: SHARE_SLICE_COLORS[Math.min(i, SHARE_SLICE_COLORS.length - 1)],
      })
      return
    }
    const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0
    segs.push({
      key: keys[i],
      d: `M ${point(a0)} A ${r} ${r} 0 ${large} 1 ${point(a1)}`,
      color: SHARE_SLICE_COLORS[Math.min(i, SHARE_SLICE_COLORS.length - 1)],
    })
  })
  return segs
}

// ─── Donut ──────────────────────────────────────────────────────────────────

const DONUT = { size: 132, r: 56, stroke: 16, gap: 0.015 }

/**
 * Donut + legend (Paper: 132px ring, 16px stroke, muted track, center total).
 * The uncovered rest of the ring stays the muted track.
 */
export function ShareDonut({
  slices,
  total,
  totalLabel,
  coverage,
  colors = SHARE_SLICE_COLORS,
  onSliceClick,
  className,
}: {
  slices: ShareSlice[]
  /** Center number: the denominator the shares were taken against */
  total: number
  /** Center caption, e.g. "unique visitors" */
  totalLabel: string
  /** One muted line under the legend naming what the slices cover */
  coverage?: ReactNode
  colors?: string[]
  onSliceClick?: (key: string) => void
  className?: string
}) {
  const [active, setActive] = useState<string | null>(null)
  const weights = sliceWeights(slices)
  const c = DONUT.size / 2
  const arcs = buildArcs(
    weights,
    { cx: c, cy: c, r: DONUT.r, start: -Math.PI / 2, sweep: 2 * Math.PI, gap: DONUT.gap, flipY: false },
    slices.map((s) => s.key),
  ).map((a) => ({ ...a, color: colors[Math.min(slices.findIndex((s) => s.key === a.key), colors.length - 1)] }))

  return (
    <div className={cn('flex flex-col items-center gap-6 px-[18px] pt-1 pb-4 sm:flex-row', className)}>
      <div className="relative shrink-0" style={{ width: DONUT.size, height: DONUT.size }}>
        <svg
          width={DONUT.size}
          height={DONUT.size}
          viewBox={`0 0 ${DONUT.size} ${DONUT.size}`}
          fill="none"
          role="img"
          aria-label={`${formatNumeric(total)} ${totalLabel}`}
          className="absolute inset-0"
        >
          <circle cx={c} cy={c} r={DONUT.r} stroke="var(--muted)" strokeWidth={DONUT.stroke} />
          {arcs.map((arc) => (
            <path
              key={arc.key}
              d={arc.d}
              stroke={arc.color}
              strokeWidth={DONUT.stroke}
              className={cn('transition-opacity duration-150', onSliceClick && 'cursor-pointer')}
              style={{ opacity: active && active !== arc.key ? 0.45 : 1 }}
              onMouseEnter={() => setActive(arc.key)}
              onMouseLeave={() => setActive(null)}
              onClick={() => onSliceClick?.(arc.key)}
            />
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <span className="text-[22px] font-semibold leading-6 tracking-[-0.02em] text-foreground tabular-nums">
            {formatNumeric(total)}
          </span>
          <span className="text-xs leading-[14px] text-muted-foreground">{totalLabel}</span>
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        {slices.map((slice, i) => (
          <button
            key={slice.key}
            type="button"
            disabled={!onSliceClick || slice.key === '__other'}
            onClick={() => onSliceClick?.(slice.key)}
            onMouseEnter={() => setActive(slice.key)}
            onMouseLeave={() => setActive(null)}
            className={cn(
              'flex h-7 items-center gap-2 text-left transition-opacity',
              onSliceClick && slice.key !== '__other' ? 'cursor-pointer' : 'cursor-default',
              active && active !== slice.key && 'opacity-60',
            )}
          >
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-[2px]"
              style={{ backgroundColor: colors[Math.min(i, colors.length - 1)] }}
            />
            {slice.leading}
            <span className="min-w-0 flex-1 truncate text-[13px] leading-4 text-foreground">{slice.label}</span>
            <span className="w-14 shrink-0 text-right text-[13px] font-medium leading-4 text-foreground tabular-nums">
              {formatNumeric(slice.value)}
            </span>
            <span className="w-10 shrink-0 text-right text-xs leading-4 text-muted-foreground tabular-nums">
              {slice.share == null ? '' : formatSliceShare(slice.share)}
            </span>
          </button>
        ))}
        {coverage && <p className="pt-1.5 text-xs leading-4 text-muted-foreground">{coverage}</p>}
      </div>
    </div>
  )
}

// ─── Semicircle gauge ───────────────────────────────────────────────────────

const GAUGE = { width: 260, height: 124, cx: 130, cy: 114, r: 100, stroke: 18, gap: 0.0125 }

/**
 * Semicircle gauge (Paper: 260×124, r 100, 18px stroke, muted track, total
 * centered in the bowl). Legend is the caller's: the Channels card keeps the
 * same legend under both views so a channel keeps its color across the toggle.
 */
export function ShareGauge({
  slices,
  colors = SHARE_SLICE_COLORS,
  total,
  totalLabel,
  className,
}: {
  slices: ShareSlice[]
  colors?: string[]
  total: number
  totalLabel: string
  className?: string
}) {
  const weights = sliceWeights(slices)
  const arcs = buildArcs(
    weights,
    { cx: GAUGE.cx, cy: GAUGE.cy, r: GAUGE.r, start: Math.PI, sweep: Math.PI, gap: GAUGE.gap, flipY: true },
    slices.map((s) => s.key),
  ).map((a) => ({ ...a, color: colors[Math.min(slices.findIndex((s) => s.key === a.key), colors.length - 1)] }))

  return (
    <div className={cn('relative shrink-0', className)} style={{ width: GAUGE.width, height: GAUGE.height }}>
      <svg
        width={GAUGE.width}
        height={GAUGE.height}
        viewBox={`0 0 ${GAUGE.width} ${GAUGE.height}`}
        fill="none"
        role="img"
        aria-label={`${formatNumeric(total)} ${totalLabel}`}
        className="absolute inset-0"
      >
        <path
          d={`M ${GAUGE.cx - GAUGE.r} ${GAUGE.cy} A ${GAUGE.r} ${GAUGE.r} 0 0 1 ${GAUGE.cx + GAUGE.r} ${GAUGE.cy}`}
          stroke="var(--muted)"
          strokeWidth={GAUGE.stroke}
        />
        {arcs.map((arc) => (
          <path key={arc.key} d={arc.d} stroke={arc.color} strokeWidth={GAUGE.stroke} />
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-x-0 bottom-1.5 flex flex-col items-center gap-0.5">
        <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
          {formatNumeric(total)}
        </span>
        <span className="text-xs leading-4 text-muted-foreground">{totalLabel}</span>
      </div>
    </div>
  )
}
